import type {
  FontDescription,
  HorizontalTextSpec,
  VerticalTextSpec,
} from "./sdfText";

// Based on https://github.com/evanw/font-texture-generator/blob/gh-pages/example-webgl/index.html
export function generateTextMesh(
  font: FontDescription,
  { text, align }: HorizontalTextSpec,
): {
  vertices: number[];
  letterIndices: number[];
} {
  // Measure the width of the text
  let totalX = 0;
  let totalY = font.size;
  let maxWidth = 0;

  for (const char of text) {
    if (char === "\n") {
      totalY += font.size;
      maxWidth = Math.max(maxWidth, totalX);
      totalX = 0;
      continue;
    }

    const character = font.characters[char];

    if (!character) {
      throw new Error(`Character "${char}" not found in font "${font.name}"`);
    }

    totalX += character.advance;
  }
  maxWidth = Math.max(maxWidth, totalX);

  // Apply alignment
  let x = 0;
  switch (align ?? "center") {
    case "left":
      break;
    case "center":
      x = -maxWidth / 2;
      break;
    case "right":
      x = -maxWidth;
      break;
  }
  const initialX = x;

  let y = 0;
  const vertices: number[] = [];
  const letterIndices: number[] = [];

  let i = 0;

  for (const char of text) {
    if (char === "\n") {
      x = initialX;
      y += font.size;
      continue;
    }

    i += 1;

    // SAFETY: We know this character exists because we checked above
    const c = font.characters[char]!;

    let x0 = x - c.originX;
    let y0 = y - c.originY;
    let s0 = c.x / font.width;
    let t0 = c.y / font.height;

    let x1 = x - c.originX + c.width;
    let y1 = y - c.originY;
    let s1 = (c.x + c.width) / font.width;
    let t1 = c.y / font.height;

    let x2 = x - c.originX;
    let y2 = y - c.originY + c.height;
    let s2 = c.x / font.width;
    let t2 = (c.y + c.height) / font.height;

    let x3 = x - c.originX + c.width;
    let y3 = y - c.originY + c.height;
    let s3 = (c.x + c.width) / font.width;
    let t3 = (c.y + c.height) / font.height;

    // swap y positions because i don't know why
    y0 = -y0;
    y1 = -y1;
    y2 = -y2;
    y3 = -y3;

    vertices.push(x0, y0, s0, t0);
    vertices.push(x1, y1, s1, t1);
    vertices.push(x3, y3, s3, t3);

    vertices.push(x0, y0, s0, t0);
    vertices.push(x3, y3, s3, t3);
    vertices.push(x2, y2, s2, t2);

    for (let vI = 0; vI < 6; vI++) {
      letterIndices.push(i);
    }

    x += c.advance;
  }

  return { vertices, letterIndices };
}

/**
 * Generates a mesh for vertical text
 */
export function generateVerticalTextMesh(
  font: FontDescription,
  { text, spaceHeight, verticalSpacing }: VerticalTextSpec,
): {
  vertices: number[];
  letterIndices: number[];
} {
  let maxWidth = 0;
  let totalY = 0;

  for (const char of text) {
    if (char === "\n" || char === " ") {
      totalY += spaceHeight;
      continue;
    }

    const character = font.characters[char];
    if (!character) {
      throw new Error(`Character "${char}" not found in font "${font.name}"`);
    }

    maxWidth = Math.max(maxWidth, character.width);
    totalY += character.height + (totalY > 0 ? verticalSpacing : 0);
  }

  // Vertical text is always centered
  let x = -maxWidth / 2;
  let y = -totalY / 2;

  const vertices: number[] = [];
  const letterIndices: number[] = [];

  let i = 0;
  for (const char of text) {
    if (char === "\n" || char === " ") {
      y += spaceHeight;
      continue;
    }

    i += 1;

    // SAFETY: We know this character exists because we checked above
    const c = font.characters[char]!;

    // Center the character
    const charX = x + (maxWidth - c.width) / 2;

    let x0 = charX - c.originX;
    let y0 = y - c.originY;
    let s0 = c.x / font.width;
    let t0 = c.y / font.height;

    let x1 = charX - c.originX + c.width;
    let y1 = y - c.originY;
    let s1 = (c.x + c.width) / font.width;
    let t1 = c.y / font.height;

    let x2 = charX - c.originX;
    let y2 = y - c.originY + c.height;
    let s2 = c.x / font.width;
    let t2 = (c.y + c.height) / font.height;

    let x3 = charX - c.originX + c.width;
    let y3 = y - c.originY + c.height;
    let s3 = (c.x + c.width) / font.width;
    let t3 = (c.y + c.height) / font.height;

    // swap y positions because i don't know why
    y0 = -y0;
    y1 = -y1;
    y2 = -y2;
    y3 = -y3;

    vertices.push(x0, y0, s0, t0);
    vertices.push(x1, y1, s1, t1);
    vertices.push(x3, y3, s3, t3);

    vertices.push(x0, y0, s0, t0);
    vertices.push(x3, y3, s3, t3);
    vertices.push(x2, y2, s2, t2);

    for (let vI = 0; vI < 6; vI++) {
      letterIndices.push(i);
    }

    y += c.height + verticalSpacing;
  }

  return { vertices, letterIndices };
}
